import React, { useEffect } from 'react';

/**
 * SEOHead Component
 * Dynamically sets meta tags, Open Graph tags, and JSON-LD structured data
 * 
 * @param {string} title - Page title
 * @param {string} description - Meta description
 * @param {string} keywords - Meta keywords
 * @param {string} ogImage - Open Graph image URL
 * @param {string} ogType - Open Graph type (website, article, product)
 * @param {string} canonicalUrl - Canonical URL
 * @param {object} structuredData - JSON-LD structured data object
 */
function SEOHead({ 
  title, 
  description, 
  keywords, 
  ogImage, 
  ogType = 'website',
  canonicalUrl,
  structuredData,
  noIndex = false
}) {
  useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Helper to set/update meta tag
    const setMeta = (name, content, isProperty = false) => {
      if (!content) return;
      
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Helper to set link tag
    const setLink = (rel, href) => {
      if (!href) return;
      
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Set standard meta tags
    setMeta('description', description);
    setMeta('keywords', keywords);
    
    // Robots
    if (noIndex) {
      setMeta('robots', 'noindex, nofollow');
    } else {
      setMeta('robots', 'index, follow');
    }

    // Open Graph tags
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:image', ogImage, true);
    if (canonicalUrl) {
      setMeta('og:url', canonicalUrl, true);
    }
    setMeta('og:site_name', 'ZOBOX', true);
    setMeta('og:locale', 'en_IN', true);

    // Twitter Card tags
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', ogImage);

    // Canonical URL
    setLink('canonical', canonicalUrl);

    // JSON-LD Structured Data
    if (structuredData) {
      // Remove existing JSON-LD
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }

      // Add new JSON-LD
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup on unmount
    return () => {
      // We don't remove meta tags on unmount as they should persist
      // until replaced by the next page's SEO component
    };
  }, [title, description, keywords, ogImage, ogType, canonicalUrl, structuredData, noIndex]);

  return null; // This component doesn't render anything
}

/**
 * Generate Organization structured data
 */
export const generateOrganizationSchema = (settings) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "ZOBOX",
  "alternateName": "Zobox Protech Limited",
  "url": "https://zobox.in",
  "logo": settings?.logo_url || "https://zobox.in/logo.png",
  "description": settings?.meta_description || "Buy certified refurbished phones and laptops at best prices",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": settings?.address_corporate || "E218C, Sector - 63",
    "addressLocality": "Noida",
    "addressRegion": "Uttar Pradesh",
    "postalCode": "201301",
    "addressCountry": "IN"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": settings?.phone || "+91-91111-77118",
    "contactType": "customer service",
    "email": settings?.email || "support@zobox.in",
    "availableLanguage": ["English", "Hindi"]
  },
  "sameAs": [
    settings?.social_facebook,
    settings?.social_instagram,
    settings?.social_twitter,
    settings?.social_linkedin,
    settings?.social_youtube
  ].filter(Boolean)
});

/**
 * Generate WebSite structured data with search action
 */
export const generateWebsiteSchema = (settings) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "ZOBOX",
  "url": "https://zobox.in",
  "description": settings?.meta_description,
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://zobox.in/shop/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
});

/**
 * Generate Product structured data
 */
export const generateProductSchema = (product) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": product.name,
  "description": product.description,
  "image": product.images || [],
  "brand": {
    "@type": "Brand",
    "name": product.brand
  },
  "offers": {
    "@type": "Offer",
    "url": product.url,
    "priceCurrency": "INR",
    "price": product.price,
    "priceValidUntil": new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    "availability": product.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    "seller": {
      "@type": "Organization",
      "name": "ZOBOX"
    }
  },
  "aggregateRating": product.rating ? {
    "@type": "AggregateRating",
    "ratingValue": product.rating,
    "reviewCount": product.reviewCount || 1
  } : undefined
});

/**
 * Generate BreadcrumbList structured data
 */
export const generateBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

/**
 * Generate LocalBusiness structured data
 */
export const generateLocalBusinessSchema = (settings) => ({
  "@context": "https://schema.org",
  "@type": "Store",
  "name": "ZOBOX",
  "image": settings?.logo_url,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": settings?.address_corporate,
    "addressLocality": "Noida",
    "addressRegion": "Uttar Pradesh",
    "postalCode": "201301",
    "addressCountry": "IN"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 28.6139,
    "longitude": 77.2090
  },
  "url": "https://zobox.in",
  "telephone": settings?.phone,
  "priceRange": "₹₹",
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    "opens": "10:00",
    "closes": "19:00"
  }
});

/**
 * Generate FAQ structured data
 */
export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export default SEOHead;
